import { useCallback, useState } from 'react';
import { LocalStorageHandler } from 'utils/LocalStorageHandler';

/**
 * `useState` that persists to localStorage, so a view-level setting survives
 * navigating between runs (and a page reload) instead of resetting to its
 * default. Reads once on mount; writes on every set.
 *
 * `revive` validates whatever was stored — anything stale, corrupt or from an
 * older shape should return `undefined`, and the fallback is used instead. Never
 * trust the stored value: it outlives the code that wrote it.
 *
 * Values whose valid range depends on loaded data (a slider bound by a run's max
 * speed, say) should be stored raw and **clamped where they are read**, not
 * clamped on write — writing the clamped value back would quietly destroy the
 * user's preference for every other run.
 */
export function useStoredState<T>(
  key: string,
  fallback: T,
  revive?: (raw: unknown) => T | undefined,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = LocalStorageHandler.get<unknown>(key);
      if (raw === null || raw === undefined) return fallback;
      if (!revive) return raw as T;
      const revived = revive(raw);
      return revived === undefined ? fallback : revived;
    } catch (error) {
      console.warn(`Failed to read stored setting ${key}:`, error);
      return fallback;
    }
  });

  const store = useCallback((next: T) => {
    setValue(next);
    try {
      LocalStorageHandler.save(key, next);
    } catch (error) {
      console.warn(`Failed to persist setting ${key}:`, error);
    }
  }, [key]);

  return [value, store];
}
